import {
  BaseGtmEvent,
  ClearEcommerceEvent,
  FavouriteEvent,
  GtmEventType,
  GtmNavigationEvent,
  GtmPromotion,
  ItemEvent,
  PageViewEvent,
  PromotionEvent,
  QuantityEvent
} from 'store/googleTagManager/types';
import { BasePageModel } from 'models/BasePageModel';
import { mapExperimentsToString } from './utils';
import {
  GtmFavouriteAction,
  GtmItemAction,
  GtmPopularCategoryNavAction,
  GtmPromotionAction,
  GtmQuantityAction,
  GtmThecaPromotionAction,
  GtmViewItemAction
} from 'store/googleTagManager/actions';
import { AppShellManifest } from 'models/AppShellManifest';
import { PageState } from 'models/PageState';

export const getPageViewEvent = (currentPage: BasePageModel): PageViewEvent => {
  const pageType = currentPage.pageName || currentPage.componentName;
  const experimentString = currentPage.meta
    ? mapExperimentsToString(currentPage.meta.experiments)
    : '';

  return {
    event: 'page_view',
    page_type: pageType,
    experiment_string: experimentString
  };
};
export const getClearEcommerceEvent = (): ClearEcommerceEvent => {
  return {
    event: 'ecommerce_clear',
    ecommerce: null
  };
};

export const getSelectItemEvent = (
  action: GtmItemAction,
  currencyCode: string
): [ClearEcommerceEvent, ItemEvent] => {
  return [
    getClearEcommerceEvent(),
    {
      event: 'select_item',
      ecommerce: {
        currency: currencyCode,
        itemListPageNbr: action.listPageNbr,
        items: [action.gtmItemProduct]
      }
    }
  ];
};
export const getViewListItemEvent = (
  action: GtmViewItemAction,
  currencyCode: string
): [ClearEcommerceEvent, ItemEvent] => {
  return [
    getClearEcommerceEvent(),
    {
      event: 'view_item_list',
      ecommerce: {
        currency: currencyCode,
        itemListPageNbr: action.listPageNbr,
        items: action.gtmItemProducts
      }
    }
  ];
};

const getSinglePromotion = (
  promotion: GtmPromotion,
  event: GtmEventType,
  pageType: string
): PromotionEvent => {
  const { creative_slot, creative_name, promotion_name, promotion_id } = promotion;

  return {
    event,
    ecommerce: {
      promotion_id,
      promotion_name,
      creative_name,
      creative_slot,
      page_type: pageType
    }
  };
};

export const getViewPromotionEvent = (
  action: GtmPromotionAction,
  prevState: PageState
): [ClearEcommerceEvent, PromotionEvent] => {
  return [
    getClearEcommerceEvent(),
    getSinglePromotion(action.gtmPromotion, 'view_promotion', prevState.currentPage.componentName)
  ];
};

export const getViewPromotionListEvents = (
  action: GtmThecaPromotionAction,
  prevState: PageState
) => {
  return action.gtmPromotions.map(promotion => {
    return [
      getClearEcommerceEvent(),
      getSinglePromotion(promotion, 'view_promotion', prevState.currentPage.componentName)
    ];
  });
};

export const getSelectPromotionEvent = (
  action: GtmPromotionAction,
  prevState: PageState
): [ClearEcommerceEvent, PromotionEvent] => {
  return [
    getClearEcommerceEvent(),
    getSinglePromotion(action.gtmPromotion, 'select_promotion', prevState.currentPage.componentName)
  ];
};

export const getAddFavouriteEvent = (
  { gtmProduct }: GtmFavouriteAction,
  currency: string
): [ClearEcommerceEvent, FavouriteEvent] => {
  return [
    getClearEcommerceEvent(),
    {
      event: 'add_to_wishlist',
      ecommerce: {
        items: [gtmProduct],
        currency,
        value: gtmProduct.price
      }
    }
  ];
};

export const getRemoveFavouriteEvent = (
  { gtmProduct }: GtmFavouriteAction,
  currency: string
): [ClearEcommerceEvent, FavouriteEvent] => {
  return [
    getClearEcommerceEvent(),
    {
      event: 'remove_from_wishlist',
      ecommerce: {
        items: [gtmProduct],
        currency,
        value: gtmProduct.price
      }
    }
  ];
};

export const getViewMoreProductsEvent = (): BaseGtmEvent => {
  return {
    event: 'view_more_products'
  };
};

export const getLoginEvent = (): BaseGtmEvent => {
  return {
    event: 'login'
  };
};

export const getQuotationRequestSubmitEvent = (): BaseGtmEvent => {
  return {
    event: 'quotation_request_submit'
  };
};

export const getPopularCategoryNavEvent = (
  { link_url, title }: GtmPopularCategoryNavAction,
  { links }: AppShellManifest
): GtmNavigationEvent => {
  const fullUrl = links.site + link_url;

  return {
    event: 'popular_category_nav',
    link_url: fullUrl,
    title
  };
};

export const getEditQuantityEvent = (
  { gtmProduct, quantity, prevQuantity }: GtmQuantityAction,
  currency: string
): [ClearEcommerceEvent, QuantityEvent] => {
  return [
    getClearEcommerceEvent(),
    {
      event: 'edit_quantity',
      ecommerce: {
        items: [gtmProduct],
        currency,
        quantity,
        prevQuantity
      }
    }
  ];
};
